import { Box, Button, Flex, Grid, Spinner, Text, useToast } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import {
  FaBoxes,
  FaClock,
  FaDollarSign,
  FaExclamationTriangle,
  FaFileInvoiceDollar,
  FaFilter,
  FaHistory,
  FaMoneyBillWave,
  FaPlaneArrival,
  FaShip,
} from "react-icons/fa";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";
import Layout from "../../../components/Layout";
import FilterModal from "../../../components/FilterModal";
import { useCompany } from "../../../contexts/CompanyContext";
import { ManagementSummary } from "../../../models/Management";
import { getSummary } from "../../../services/managementService";
import { crm } from "../../../theme";
import ActiveFiltersBar from "../components/ActiveFiltersBar";
import { managementFilterTypes, useManagementFilters } from "../components/useManagementFilters";

const usdFmt = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const brlFmt = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });

const KpiTile = ({
  title,
  value,
  icon,
  tone,
  onClick,
}: {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  tone?: "danger" | "brand";
  onClick?: () => void;
}) => (
  <Box
    onClick={onClick}
    cursor={onClick ? "pointer" : undefined}
    _hover={onClick ? { borderColor: crm.brand } : undefined}
    bg={crm.surface}
    borderRadius="16px"
    p="20px"
    boxShadow={crm.shadowSm}
    border={`1px solid ${tone === "danger" ? "#fecaca" : crm.border}`}
  >
    <Flex align="center" gap="10px" mb="10px">
      <Flex
        align="center"
        justify="center"
        w="36px"
        h="36px"
        borderRadius="10px"
        bg={tone === "danger" ? "#fef2f2" : crm.brandSoft}
        color={tone === "danger" ? crm.danger : crm.brand}
        fontSize="16px"
        flexShrink={0}
      >
        {icon}
      </Flex>
      <Text fontSize="sm" color={crm.textMuted} fontWeight="500">
        {title}
      </Text>
    </Flex>
    <Text fontSize="28px" fontWeight="700" color={crm.text} lineHeight="1">
      {value}
    </Text>
  </Box>
);

const Painel = () => {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const navigate = useNavigate();
  const toast = useToast();
  const { currentCompany } = useCompany();
  const {
    tags,
    setTags,
    filterParams,
    activeFilterCount,
    activeFilterChips,
    clearAllFilters,
    visibleExtraFilters,
    extraFilters,
    filtersOpen,
    onOpenFilters,
    onCloseFilters,
    deps: filterDeps,
  } = useManagementFilters();

  const [data, setData] = useState<ManagementSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const loadSeq = useRef(0);

  const load = async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    try {
      const result = await getSummary(filterParams);
      if (seq !== loadSeq.current) return; // resposta de uma chamada mais antiga — ignora
      setData(result);
    } catch (error) {
      if (seq !== loadSeq.current) return;
      console.log("error", error);
      toast({ title: "Não foi possível carregar o painel.", status: "error" });
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [currentCompany, ...filterDeps]);

  return (
    <Box height={isMobile ? undefined : "100vh"} overflow="auto">
      <Layout>
        <Box p="24px" bg={crm.pageBg} minH="100%">
          <Flex align="center" justify="space-between" mb="24px" flexWrap="wrap" gap="12px">
            <Flex align="center" gap="12px">
              <Box w="4px" h="32px" bg={crm.brand} borderRadius="full" />
              <Box>
                <Text fontSize="28px" fontWeight="700" color={crm.text} lineHeight="1.1">
                  Painel de Gestão
                </Text>
                <Text color={crm.textMuted} fontSize="sm" mt="2px">
                  Situação geral da operação Comex.
                </Text>
              </Box>
            </Flex>
            <Button
              leftIcon={<FaFilter />}
              variant="outline"
              borderColor={activeFilterCount > 0 ? crm.brand : crm.border}
              color={activeFilterCount > 0 ? crm.brand : crm.text}
              _hover={{ bg: crm.brandSoft, borderColor: crm.brand }}
              borderRadius="10px"
              fontWeight="500"
              onClick={onOpenFilters}
            >
              Filtros{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
            </Button>
          </Flex>

          <ActiveFiltersBar chips={activeFilterChips} onClear={clearAllFilters} />

          {loading && (
            <Flex justify="center" py="48px">
              <Spinner size="lg" color={crm.brand} />
            </Flex>
          )}

          {!loading && data && (
            <>
              <Grid templateColumns={isMobile ? "1fr" : "repeat(4, 1fr)"} gap="16px" mb="16px">
                <KpiTile title="Processos ativos" value={data.ativos} icon={<FaBoxes />} />
                <KpiTile title="Em trânsito" value={data.emTransito} icon={<FaShip />} />
                <KpiTile title="Em desembaraço" value={data.emDesembaraco} icon={<FaBoxes />} />
                <KpiTile
                  title="Atrasados"
                  value={data.atrasados}
                  icon={<FaExclamationTriangle />}
                  tone={data.atrasados > 0 ? "danger" : undefined}
                />
              </Grid>

              <Grid templateColumns={isMobile ? "1fr" : "repeat(4, 1fr)"} gap="16px" mb="16px">
                <KpiTile title="Chegadas em 7 dias" value={data.chegadas7Dias} icon={<FaPlaneArrival />} />
                <KpiTile title="FOB em andamento (USD)" value={usdFmt.format(data.fobEmAndamentoUsd)} icon={<FaDollarSign />} />
                <KpiTile title="Custo total estimado (R$)" value={brlFmt.format(data.custoTotalEstimadoBrl)} icon={<FaMoneyBillWave />} />
                <KpiTile
                  title="Pendências críticas"
                  value={data.pendenciasCriticas}
                  icon={<FaClock />}
                  tone={data.pendenciasCriticas > 0 ? "danger" : undefined}
                />
              </Grid>

              {data.purchaseOrders && (
                <>
                  <Text fontSize="xs" color={crm.textMuted} fontWeight="600" mb="8px" textTransform="uppercase">
                    Ordens de compra · todas as empresas
                  </Text>
                  <Grid templateColumns={isMobile ? "1fr" : "repeat(3, 1fr)"} gap="16px" mb="16px">
                    <KpiTile
                      title="POs sem processo"
                      value={data.purchaseOrders.semProcesso}
                      icon={<FaFileInvoiceDollar />}
                      onClick={() => navigate("/gestao/funil")}
                    />
                    <KpiTile
                      title="FOB de POs sem processo (USD)"
                      value={usdFmt.format(data.purchaseOrders.fobSemProcessoUsd)}
                      icon={<FaDollarSign />}
                      onClick={() => navigate("/gestao/funil")}
                    />
                    <KpiTile
                      title="Alterações de PO pendentes"
                      value={data.purchaseOrders.alteracoesPendentes}
                      icon={<FaHistory />}
                      tone={data.purchaseOrders.alteracoesPendentes > 0 ? "danger" : undefined}
                      onClick={() => navigate("/purchaseOrders?pending=1")}
                    />
                  </Grid>
                </>
              )}

              {/* Barra resumo do funil — clicável, leva pro kanban */}
              <Box
                bg={crm.surface}
                borderRadius="16px"
                p="20px"
                boxShadow={crm.shadowSm}
                border={`1px solid ${crm.border}`}
              >
                <Text fontWeight="700" color={crm.text} fontSize="sm" mb="14px">
                  Processos por etapa
                </Text>
                <Flex gap="10px" flexWrap="wrap">
                  {data.porEtapa.map((stage) => (
                    <Flex
                      key={stage.stageId ?? "sem-etapa"}
                      align="center"
                      gap="8px"
                      px="14px"
                      py="10px"
                      borderRadius="10px"
                      bg={crm.subtle}
                      border={`1px solid ${crm.border}`}
                      cursor="pointer"
                      _hover={{ borderColor: crm.brand, bg: crm.brandSoft }}
                      onClick={() => navigate("/gestao/funil")}
                    >
                      <Box w="8px" h="8px" borderRadius="full" bg={stage.color || crm.brand} flexShrink={0} />
                      <Text fontSize="sm" color={crm.text} fontWeight="500">
                        {stage.stageName}
                      </Text>
                      <Text fontSize="sm" color={crm.textMuted} fontWeight="700">
                        {stage.count}
                      </Text>
                    </Flex>
                  ))}
                  {data.porEtapa.length === 0 && (
                    <Text color={crm.textFaint} fontSize="sm">
                      Nenhum processo ativo no momento.
                    </Text>
                  )}
                </Flex>
              </Box>
            </>
          )}
        </Box>

        <FilterModal
          isOpen={filtersOpen}
          onClose={onCloseFilters}
          tags={tags}
          setTags={setTags}
          extraFilters={[...visibleExtraFilters, ...extraFilters]}
          types={managementFilterTypes}
        />
      </Layout>
    </Box>
  );
};

export default Painel;
